import { useEffect, useRef, useState } from "react";
import { Box, Collapse, IconButton, Typography } from "@mui/material";
import { Plus, Minus } from "lucide-react";
import {
  useTaskAgentState,
  FunctionCall,
  processEventsWithAddedData,
} from "components/hooks/useTaskAgentState";
import { GetAgentsThreadResponse } from "protogen/agents_service_pb";
import { ActionDisplay } from "components/tools/actions/ActionDisplay";
import { PlanWithActionsSection } from "components/tools/PlanWithActionsSection";

interface InlineAgentDisplayProps {
  agentThreadData?: GetAgentsThreadResponse;
  defaultExpanded?: boolean;
  capturedCalls?: FunctionCall[];
}

export default ({
  agentThreadData,
  defaultExpanded = false,
  capturedCalls,
}: InlineAgentDisplayProps) => {
  const hasLoaded = useRef(false);
  const [ready, setReady] = useState(false);
  const stateManagerRef = useRef<ReturnType<typeof useTaskAgentState> | null>(
    null,
  );

  const stateManager = useTaskAgentState({ accountType: "member" });
  stateManagerRef.current = stateManager;

  useEffect(() => {
    // Skip event replay when we already have captured live data.
    if (capturedCalls !== undefined || hasLoaded.current || !agentThreadData)
      return;
    hasLoaded.current = true;
    if (!stateManagerRef.current) return;

    stateManagerRef.current.actions.updateState({
      clientOutputVisible: false,
      playbookRef: agentThreadData.playbookRef || undefined,
    });

    const addedEventsMap = new Map<
      number,
      {
        eventRef: string;
        eventStatus: string;
        eventModified: boolean;
        sourceEntityType: string;
        sourceEntityId: string;
        sourceEntityIndex: number;
      }
    >();
    if (agentThreadData.addedEvents?.length) {
      agentThreadData.addedEvents.forEach((e) => {
        addedEventsMap.set(e.sourceEntityIndex, {
          eventRef: e.eventRef,
          eventStatus: e.eventStatus,
          eventModified: e.eventModified,
          sourceEntityType: e.sourceEntityType,
          sourceEntityId: e.sourceEntityId,
          sourceEntityIndex: e.sourceEntityIndex,
        });
      });
    }

    const processed = processEventsWithAddedData(
      agentThreadData.events || [],
      addedEventsMap,
    );
    processed.forEach((eventString) => {
      const event = JSON.parse(eventString);
      stateManagerRef.current?.actions.handleWebSocketMessage("", event);
    });

    setReady(true);
  }, [agentThreadData]);

  const { planWithActions, unassociatedActions } = stateManager.computed;
  const hasContent =
    unassociatedActions.length > 0 || planWithActions.length > 0;
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (capturedCalls !== undefined) {
    if (capturedCalls.length === 0) return null;
    return (
      <Box sx={{ width: "100%", marginBottom: "6px" }}>
        {capturedCalls.map((call) => (
          <ActionDisplay key={call.id} call={call} accountType="member" />
        ))}
      </Box>
    );
  }

  if (!ready || !hasContent) return null;

  const actionCount =
    unassociatedActions.length +
    planWithActions.reduce((sum, p) => sum + p.actions.length, 0);
  const stepCount = planWithActions.filter(
    (p) => p.plan.status === "completed" || p.plan.status === "in_progress",
  ).length;

  const actionLabel =
    actionCount === 0
      ? "no actions"
      : `${actionCount} action${actionCount !== 1 ? "s" : ""}`;
  const summary =
    stepCount > 0
      ? `${stepCount} step${stepCount !== 1 ? "s" : ""}, ${actionLabel}`
      : actionLabel;

  return (
    <Box
      sx={{
        width: "100%",
        marginBottom: "8px",
        border: "1px solid #EAEBEC",
        borderRadius: "12px",
        padding: "8px 14px",
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        sx={{ cursor: "pointer" }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="bodySmall" color="text.secondary" flex="1">
          {summary}
        </Typography>
        <IconButton size="small" sx={{ padding: "4px" }}>
          {expanded ? (
            <Minus size={16} stroke="#8E9598" />
          ) : (
            <Plus size={16} stroke="#8E9598" />
          )}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ marginTop: "8px" }}>
          <PlanWithActionsSection
            planWithActions={planWithActions}
            unassociatedActions={unassociatedActions}
            accountType="member"
            playbookRef={stateManager.state.playbookRef}
            showActions={true}
            hideDividers={true}
          />
        </Box>
      </Collapse>
    </Box>
  );
};
